import castErrorToBaseError from '@/functions/castErrorToBaseError';
import config from '@/modules/config/functions/config';
import Error404 from '@/modules/core/routes/error-404';
import { PromotionGetNotFoundError } from '@/modules/errors/constants/error-class-map.gen';
import SummaryStep from '@/modules/promotions/components/form-steps/SummaryStep';
import promotionCreationFormDefaultValues from '@/modules/promotions/constants/promotion-form-default-values';
import usePromotionFormValidationSchema from '@/modules/promotions/hooks/usePromotionFormValidationSchema';
import usePromotionQuery from '@/modules/promotions/queries/usePromotionQuery';
import type { PromotionImage } from '@/modules/promotions/types/promotion-form-image';
import type { PromotionEditFormValues } from '@/modules/promotions/types/PromotionEditFormValues';
import { Helmet } from '@dr.pogodin/react-helmet';
import { joiResolver } from '@hookform/resolvers/joi';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Button,
  ErrorPage,
  isEmptyString,
  Skeleton,
  Typography,
} from '@thisisdmg/tirecloud-patterns/v2';
import { _unixToClientTime, _clientTimeFormat } from '@/functions/timezone';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import classes from './index.module.css';

const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm';
const languages = Object.values(config('i18n.languages', {}));

function PromotionDetailsPage() {
  const { t, i18n } = useTranslation();
  const id = Route.useParams({ select: (p) => p.id });
  const navigate = Route.useNavigate();

  const {
    data: promotionData,
    isLoading: isLoadingPromotion,
    error: errorFetchingPromotion,
    refetch: refetchPromotion,
  } = usePromotionQuery({
    variables: {
      params: {
        id: ~~id,
      },
    },
  });

  const promotionFetchingError = useMemo(() => {
    if (!errorFetchingPromotion) return;
    return castErrorToBaseError(errorFetchingPromotion);
  }, [errorFetchingPromotion]);

  const promotionEditFormValuesSchema = usePromotionFormValidationSchema();
  const defaultValues = useMemo(() => {
    const promotion = promotionData?.data;
    if (!promotion) return promotionCreationFormDefaultValues;

    return {
      status: promotion.status,
      name: Object.fromEntries(
        languages.map((language) => [
          language.code,
          promotion.name?.[language.code] ?? '',
        ])
      ), // locale => name
      description: Object.fromEntries(
        languages.map((language) => [
          language.code,
          promotion.description?.[language.code] ?? '',
        ])
      ), // locale => description
      promotionERPCode: promotion.promotionERPCode ?? '',
      validFrom: _clientTimeFormat(promotion.validFrom, DATETIME_FORMAT), // YYYY-MM-DD HH:mm in client timezone
      validUntil: _clientTimeFormat(promotion.validUntil, DATETIME_FORMAT), // YYYY-MM-DD HH:mm in client timezone
      promotionImages: (promotion.promotionImages ?? []).map<PromotionImage>(
        (image) => {
          return {
            uid: image.id.toString(),
            mediaData: {
              type: 'server',
              remoteId: image.id,
              source: `${config('api.url')}/var/assets${image.extImageorVideo}`,
            },
            filename: '',
            thumbnailFilename: '',
            subline: Object.fromEntries(
              languages.map((language) => [
                language.code,
                image.subline?.[language.code] ?? '',
              ])
            ),
          };
        }
      ),
      maximumUsageTotal: promotion.maximumUsageTotal ?? undefined,
      maximumUsagePerCustomer: promotion.maximumUsagePerCustomer ?? undefined,
      maximumUsageTotalValue: promotion.maximumUsageTotalValue ?? false,
      maximumUsagePerCustomerValue:
        promotion.maximumUsagePerCustomerValue ?? false,
      negateCustomerRestriction: promotion.negateCustomerRestriction ?? false,
      customerRestriction: (promotion.customerRestriction ?? []).map(
        (user) => user.id
      ),
      customerSegmentRestriction: (
        promotion.customerSegmentRestriction ?? []
      ).map((segment) => segment.id),
      negateCustomerSegmentRestriction:
        promotion.negateCustomerSegmentRestriction ?? false,
      minimumStock: promotion.minimumStock ?? undefined,
      negateMinimumStock: promotion.negateMinimumStock ?? false,
      eligibleTires: (promotion.eligibleTires ?? []).map((tire) => tire.id),
      eligibleTireCategories: (promotion.eligibleTireCategories ?? []).map(
        (category) => category.id
      ),
      eligibleRims: promotion.eligibleRims ?? [],
      eligibleBrands: (promotion.eligibleBrands ?? []).map((brand) => brand.id),
      eligiblePartTypeCodes: (promotion.eligiblePartTypeCodes ?? []).map(
        (type) => type
      ),
      tireAmount: promotion.tireAmount ?? undefined,
      freeTireCondition: promotion.freeTireCondition ?? undefined,
      priceChange: promotion.priceChange ?? undefined,
      priceChangeType: promotion.priceChangeType ?? undefined,
      freeTireAmount: promotion.freeTireAmount ?? undefined,
    };
  }, [promotionData]);

  const form = useForm<PromotionEditFormValues>({
    mode: 'all',
    values: defaultValues,
    shouldFocusError: true,
    shouldUnregister: false,
    resolver: joiResolver(promotionEditFormValuesSchema, {
      abortEarly: false,
    }),
  });

  const promotionName = useMemo(() => {
    return (
      promotionData?.data.name[i18n.language] ??
      promotionData?.data.name[config('i18n.fallbackLanguage', '')]
    );
  }, [promotionData, i18n.language]);

  return (
    <>
      <div className={classes.promotion_details_page}>
        {!isEmptyString(promotionName) ? (
          <Helmet titleTemplate={`%s - ${t('common.promotion_one')}`}>
            <title>{promotionName}</title>
          </Helmet>
        ) : (
          <Helmet>
            <title>{t('common.promotion_one')}</title>
          </Helmet>
        )}
        {isLoadingPromotion ? (
          <>
            <Skeleton active />
          </>
        ) : promotionFetchingError ? (
          promotionFetchingError instanceof PromotionGetNotFoundError ? (
            <div className={classes.error}>
              <Error404 />
            </div>
          ) : (
            <div className={classes.error}>
              <ErrorPage
                code={[5, 0, 0]}
                title={t('common.oops')}
                description={t(`errors.gen.${promotionFetchingError.path}`, {
                  errorId: promotionFetchingError.id,
                })}
                goBackButton={{
                  label: t('common.reload'),
                  onClick: () => {
                    refetchPromotion();
                  },
                }}
              />
            </div>
          )
        ) : (
          <>
            <div className={classes.header}>
              <Typography variant="hero">
                {promotionData?.data.name[i18n.language]}
              </Typography>
              {!['deleted', 'disabled'].includes(
                promotionData?.data.status ?? ''
              ) && (
                <Button.Solid
                  tag={Link}
                  to={`/promotions/edit/${promotionData?.data.id}`}
                >
                  {t('promotions.edit_promotion')}
                </Button.Solid>
              )}
            </div>
            <FormProvider {...form}>
              <SummaryStep
                isEditMode
                allowEdit={
                  !['deleted', 'disabled'].includes(
                    promotionData?.data.status ?? ''
                  )
                }
                goToStep={(step) => {
                  navigate({
                    to: `/promotions/edit/$id`,
                    params: { id: promotionData?.data.id?.toString() ?? '0' },
                    search: (prev) => ({ ...prev, s: step }),
                  });
                }}
                showOnlyFieldsWithValues
              />
            </FormProvider>
          </>
        )}
      </div>
    </>
  );
}

export const Route = createFileRoute('/_main-ui/_protected/promotions/$id')({
  component: PromotionDetailsPage,
});
